import React, { useState, useEffect } from 'react';
import { coreAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import Pagination from '../../components/Pagination';
import './Admin.css';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { success, error } = useToast();
  const { formatDateTime } = useLanguage();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await coreAPI.adminGetMessages();
      setMessages(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (err) {
      error('無法載入訊息列表');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await coreAPI.adminPatchMessage(messageId, { is_read: true });
      success('已標記為已讀');
      fetchMessages();
    } catch (err) {
      error('更新失敗');
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAsUnread = async (messageId) => {
    try {
      await coreAPI.adminPatchMessage(messageId, { is_read: false });
      success('已標記為未讀');
      fetchMessages();
    } catch (err) {
      error('更新失敗');
      console.error('Error marking as unread:', err);
    }
  };

  const handleToggleResolved = async (message) => {
    try {
      await coreAPI.adminPatchMessage(message.id, { is_resolved: !message.is_resolved });
      success(message.is_resolved ? '已標記為未處理' : '已標記為已處理');
      fetchMessages();
    } catch (err) {
      error('更新失敗');
      console.error('Error toggling resolved:', err);
    }
  };

  const filteredMessages = messages.filter(message => {
    const matchesSearch = !search ||
      message.name?.toLowerCase().includes(search.toLowerCase()) ||
      message.phone?.includes(search) ||
      message.email?.toLowerCase().includes(search.toLowerCase()) ||
      message.subject?.toLowerCase().includes(search.toLowerCase()) ||
      message.message?.toLowerCase().includes(search.toLowerCase());
      
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'unread' && !message.is_read) ||
      (statusFilter === 'read' && message.is_read) ||
      (statusFilter === 'resolved' && message.is_resolved) ||
      (statusFilter === 'unresolved' && !message.is_resolved);
      
    return matchesSearch && matchesStatus;
  });

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMessages = filteredMessages.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <LoadingPage message="載入訊息資料中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>聯絡訊息管理</h1>
        <div className="admin-actions">
          <input
            type="text"
            placeholder="搜尋姓名、電話、主旨..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="filter-tabs">
        <button 
          className={statusFilter === 'all' ? 'active' : ''}
          onClick={() => setStatusFilter('all')}
        >
          全部 ({messages.length})
        </button>
        <button 
          className={statusFilter === 'unread' ? 'active' : ''}
          onClick={() => setStatusFilter('unread')}
        >
          未讀 ({messages.filter(m => !m.is_read).length})
        </button>
        <button 
          className={statusFilter === 'read' ? 'active' : ''}
          onClick={() => setStatusFilter('read')}
        >
          已讀 ({messages.filter(m => m.is_read).length})
        </button>
        <button 
          className={statusFilter === 'unresolved' ? 'active' : ''}
          onClick={() => setStatusFilter('unresolved')}
        >
          未處理 ({messages.filter(m => !m.is_resolved).length})
        </button>
        <button 
          className={statusFilter === 'resolved' ? 'active' : ''}
          onClick={() => setStatusFilter('resolved')}
        >
          已處理 ({messages.filter(m => m.is_resolved).length})
        </button>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>姓名</th>
              <th>聯絡方式</th>
              <th>主旨</th>
              <th>訊息</th>
              <th>提交時間</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredMessages.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                  沒有符合條件的訊息
                </td>
              </tr>
            ) : (
              paginatedMessages.map(message => (
                <tr key={message.id} className={!message.is_read ? 'unread-row' : ''}>
                  <td>
                    <strong>{message.name}</strong>
                    {message.member_name && (
                      <div className="subtitle">
                        會員: {message.member_name}
                      </div>
                    )}
                  </td>
                  <td>
                    {message.phone && <div>📞 {message.phone}</div>}
                    {message.email && <div>📧 {message.email}</div>}
                  </td>
                  <td>{message.subject || '-'}</td>
                  <td>
                    <div className="message-preview">
                      {message.message.length > 100 
                        ? `${message.message.substring(0, 100)}...` 
                        : message.message}
                    </div>
                  </td>
                  <td>{formatDateTime(message.submitted_at)}</td>
                  <td>
                    <div className="status-badges">
                      <span className={`status-badge ${message.is_read ? 'status-read' : 'status-unread'}`}>
                        {message.is_read ? '已讀' : '未讀'}
                      </span>
                      <span className={`status-badge ${message.is_resolved ? 'status-resolved' : 'status-unresolved'}`}>
                        {message.is_resolved ? '已處理' : '未處理'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {!message.is_read ? (
                        <button 
                          onClick={() => handleMarkAsRead(message.id)}
                          className="btn btn-sm btn-secondary"
                        >
                          標記已讀
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleMarkAsUnread(message.id)}
                          className="btn btn-sm btn-outline"
                        >
                          標記未讀
                        </button>
                      )}
                      <button 
                        onClick={() => handleToggleResolved(message)}
                        className={`btn btn-sm ${message.is_resolved ? 'btn-warning' : 'btn-success'}`}
                      >
                        {message.is_resolved ? '標記未處理' : '標記已處理'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredMessages.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          totalItems={filteredMessages.length}
        />
      )}

      <style jsx>{`
        .unread-row {
          background-color: #f8f9fa;
          font-weight: 500;
        }
        
        .message-preview {
          max-width: 300px;
          white-space: normal;
          line-height: 1.4;
        }
        
        .status-badges {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .status-read {
          background-color: #28a745;
          color: white;
        }
        
        .status-unread {
          background-color: #dc3545;
          color: white;
        }
        
        .status-resolved {
          background-color: #007bff;
          color: white;
        }
        
        .status-unresolved {
          background-color: #ffc107;
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default ContactMessages;
